import React from 'react';

import { intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { propTypes } from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Detects if a line item is a tax line item by checking the code pattern.
 * Tax codes: line-item/{name}~tax or line-item/{name}~tax~pct-{value}
 */
export const isTaxLineItem = lineItem => {
    const code = lineItem?.code;
    if (typeof code !== 'string') return false;
    const withoutNamespace = code.replace(/^line-item\//, '');
    const withoutPct = withoutNamespace.replace(/~pct-[\d.]+$/, '');
    if (withoutPct.endsWith('~tax')) return true;
    return withoutPct === 'tax' || withoutPct.includes('tax');
};

/**
 * Extracts the tax percentage from a tax line item code.
 * E.g. "line-item/washington-tax~tax~pct-8" → 8
 */
export const extractTaxPercentage = code => {
    if (typeof code !== 'string') return null;
    const match = code.match(/~pct-([\d.]+)$/);
    if (match) {
        const parsed = parseFloat(match[1]);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

/**
 * Renders the tax line (positive) in the provider breakdown before subtotal.
 * Tax is includeFor:['customer'] only on the server, so we read from allLineItems.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.allLineItems - All unfiltered transaction line items
 * @param {boolean} props.isProvider
 * @param {intlShape} props.intl
 */
const LineItemTaxWithheldMaybe = props => {
    const { allLineItems, isProvider, intl } = props;

    if (!isProvider) return null;

    const taxLineItems = (allLineItems || []).filter(
        item => isTaxLineItem(item) && !item.reversal
    );

    if (taxLineItems.length === 0) return null;

    return (
        <>
            {taxLineItems.map((item, i) => {
                const percentage = extractTaxPercentage(item.code);
                const percentageLabel = percentage != null ? ` (${percentage}%)` : '';
                const absAmount =
                    item.lineTotal instanceof Money
                        ? new Money(Math.abs(item.lineTotal.amount), item.lineTotal.currency)
                        : null;
                const formattedAmount = absAmount ? formatMoney(intl, absAmount) : null;

                return (
                    <div key={`tax-provider-${i}`} className={css.lineItem}>
                        <span className={css.itemLabel}>{`Tax${percentageLabel}`}</span>
                        <span className={css.itemValue}>{formattedAmount}</span>
                    </div>
                );
            })}
        </>
    );
};

export default LineItemTaxWithheldMaybe;
